import { useState, useEffect, useContext } from 'react'
import {
  Box,
  Grid,
  Paper,
  SvgIcon,
  Stack,
  Typography,
  Button,
  FormControl,
  MenuItem,
  Select,
  TextField,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import { 
  DataContext, 
  DataDispatchContext 
} from '../../../contexts/data/dataContext'
import { useGetCurrentSession } from '../../../services/api-query/auth'
import { useGetFeedbackFlows } from '../../../services/api-query/feedback'
import custom from '../../../styles/mui/szVariables.json'
import { analyticsPage, analyticsDomain, analyticsCta } from '../../../services/constants'
import { sendClickAnalytics, sendViewAnalytics } from '../../common/util/util'

export default function FeedbackForm() {
  const border = custom.border
  const data = useContext(DataContext)
  const dispatch: any = useContext(DataDispatchContext)
  const [selectedIndex, setSelectedIndex] = useState(2)
  const [selectedTopic, setSelectedTopic] = useState('')
  const [selectedType, setSelectedType] = useState({} as any)
  const [description, setDescription] = useState('')
  const [allowContact, setAllowContact] = useState(true)
  const [message, setMessage] = useState([] as string[])
  const [submitting, setSubmitting] = useState(false)
  const { currentSession } = useGetCurrentSession()
  const { feedbackFlows } = useGetFeedbackFlows(currentSession);
 
  const isNotValid = (
    !description || 
    submitting ||
    (selectedType?.options?.length>0 && !selectedTopic)
  )

  function sendAnalytics(name: string) {
    sendViewAnalytics('/profile', {
          domain: analyticsDomain.feedback,
          name
        })
  }

  const handleSelectFeedbackType = (val: any, i: number) => {
    setSelectedType(val)
    setSelectedIndex(i)
    setSelectedTopic('')
    setSubmitting(false)
    setMessage([])
    sendAnalytics(analyticsPage.feedbackOptions)
    sendClickAnalytics('/profile', {
          domain: analyticsDomain.feedback,
          name: analyticsCta.selectFeedbackFlow,
          metadata: { feedbackFlowId: selectedType?.id },
        })   
  }

  function handleAllowContactClick(val: any) {
    setAllowContact(val)
    sendClickAnalytics('/profile', {
            domain: analyticsDomain.feedback,
            name: analyticsCta.canContactMe,
            metadata: {
              allowContact: val
            },
          })   
  }

  const handleSelectTopic = (e: any) => {
    setSelectedTopic(e.target.value)
    setSubmitting(false)
    setMessage([])
    sendAnalytics(analyticsPage.feedbackOptions)
  }

  const handlDescription = (e: any) => {
    setDescription(e.target.value)
    setSubmitting(false)
    setMessage([])
  }


  const submitFeedback = () => {
    setMessage([])
    setSubmitting(true)
    sendClickAnalytics('/profile', {
          domain: analyticsDomain.feedback,
          name: analyticsCta.sendFeedback,
          metadata: { 
            feedbackFlowId: selectedType?.id,
            selectedTopic
           },
        })   
    data?.submitFeedbackData({
      id: selectedType?.id,
      selectedTopic,
      description,
      allowContact
    }).then((res: string)=>{
      if(res==='submitted') {
        setMessage(['success','Feedback submitted'])
      } else {
        setMessage(['error',res])
      }
      setSubmitting(false)
    })
  }
  function handleEnterKey(ev: any) {
    if(ev.key === "Enter" && !isNotValid) {
      submitFeedback()
    }
  }

  useEffect(() => {
    if(feedbackFlows && currentSession) {
      dispatch({
        type: 'setFeedbackFlows',
        feedbackFlows,
      })
      setSelectedType(feedbackFlows?.feedbackFlows[2])
    } 
  },[
    feedbackFlows, 
    dispatch, 
    currentSession, 
])

  return (
      <Paper
        elevation={0}
        sx={{ 
          borderRadius: custom.corners4, 
          border, 
          width: '100%', 
          boxSizing: 'border-box' 
        }}>
        <Box 
          display="flex" 
          flexDirection={"column"}
          >
          <Box pl={custom.spacing.md}
              pr={custom.spacing.sm}
              pt={custom.spacing.md}
              pb={custom.spacing.xs}>
            <Typography variant="szHeading.Bold.Large">
              Send feedback
            </Typography>  
          </Box>  
          <Box m={custom.spacing.md}>
            <Typography
              variant="szHeading.Bold.xSmall">
              Feedback type
            </Typography>
            <Grid container direction="row" 
                  justifyContent="flex-start" 
                  alignContent="flex-start"
                  pt={custom.spacing.xs}
                  spacing={custom.spacing.xs}>
              {data?.feedback?.types.map(
                (feedbackType: any, i: number) => (
                <Grid item xs={12} sm={12} lg={6} key={i}>
                  <Paper
                    id={`${feedbackType.title}`.replaceAll(' ','')}
                    onClick={
                      () => handleSelectFeedbackType(feedbackType, i)
                    }
                    elevation={0}
                    sx={{
                      borderRadius: custom.corners4,
                      border: selectedIndex === i ? 
                              custom.selected : 
                              border,
                      boxSizing: 'border-box',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'sz.colors.hover',
                        boxShadow: 'none',
                      },
                    }}>
                    <Stack direction="row" 
                          alignItems="center" 
                          mx={custom.spacing.sm}
                          my={custom.spacing.xxs}
                          >
                      <SvgIcon sx={{ pl: custom.spacing.xxs }}> 
                        {feedbackType.iconPath} 
                      </SvgIcon>
                      <Typography
                        m={custom.spacing.xxs}
                        id={`${feedbackType.title}Text`
                                .replaceAll(' ','')}
                        variant="szHeading.Regular.xSmall"
                        color={
                          `${selectedIndex === i ? 
                              '#006FF4' : 'black'}`
                          }>
                        {feedbackType.title}
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            {((selectedType?.options ||[]).length>0)?
              <>
              <Box pt={custom.spacing.sm}>
                <Typography variant="szHeading.Bold.xSmall">
                  Topic
                </Typography>
              </Box>
              
              <Box pt={custom.spacing.xs}>
                <FormControl fullWidth>
                  <Select
                    labelId="feedback-topic-select-label"
                    id={`${selectedType?.title}Options`
                            .replaceAll(' ','')}
                    value={selectedTopic}
                    displayEmpty
                    slotProps={{ 
                      root: { 
                        sx: { 
                          borderRadius: custom.corners3, 
                          height: custom.sizes.xl
                        }
                      }
                    }}
                    onChange={(e) => handleSelectTopic(e)}>
                    {(selectedType?.options ||[]).map(
                      (topic: any, i: number) => (
                        <MenuItem value={topic?.id} 
                          id={`${topic?.title}`.replaceAll(' ','')}
                          key={i} 
                          sx={{ p: 2}}>
                          {topic?.title}
                        </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              </>
            :<></>}
            <Box mt={custom.spacing.xs}>
              <Typography variant="szHeading.Bold.xSmall">
                Description
              </Typography>
            </Box>
            <Box mt={custom.spacing.xs}>
              <TextField
                fullWidth
                id="feedbackDescription"
                multiline
                rows={6}
                onFocus={()=>sendAnalytics(analyticsPage.feedbackWrite)}
                InputProps={{ sx: { borderRadius: custom.corners3 }}}
                placeholder={selectedType?.placeholder}
                value={description}
                onChange={handlDescription}
              />
               <Typography color={message[0]==='success'?'success.main':'error'} 
                  id="submitMessage"
                  variant="szBody.Regular.Small">
                  {message[1]}
              </Typography>
            </Box>
            <Box mt={custom.spacing.sm}>
              <FormControlLabel
                control={<Checkbox defaultChecked
                  id="allowContactCheckbox"
                  icon={<CircleOutlinedIcon />} 
                  checkedIcon={<CheckCircleIcon />} 
                  onChange={(evt: any) => {
                    handleAllowContactClick(evt.target.value)
                }}/>}
                label={selectedType?.allowToContactTitle}
                slotProps={{ 
                  typography: { variant: 'szBody.Regular.Small'}
                }}
              />
            </Box>
            <Box my={custom.spacing.xxs} 
              width={{xs: '90%', sm:"90%", md:"90%", lg:"30%"}}>
              <Button variant="contained"
                id="submitFeedbackButton"
                fullWidth
                onKeyUp={handleEnterKey}
                onClick={submitFeedback}
                disabled={isNotValid}>
                Send
              </Button>
            </Box>
            <Box>
              <Typography variant="szBody.Regular.Small"
                id="disclaimerText" 
                sx={{ color: 'text.tertiary' }}>
                {selectedType?.disclaimer}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
  )
}
